import { useEffect, useState, type CSSProperties } from 'react';
import { confirm, EmptyState, Icon, Popover, rovingMenuEntry, rovingMenuItem, Tooltip, Trans } from '@orgazone/ui-web';
import type { CouponTitleType } from '@orgazone/sync-contract';
import { gouvernanceDe } from './parametres-modele';
import { useServedLists, useStoreSettingsDraft } from './parametres-reglage';
import {
  COUPON_TYPE_LABELS, WALLET_SETTINGS, WALLET_SWITCHES, couponBadge, walletCardView,
  type WalletConnection, type WalletHead, type WalletRowState, type WalletTemplateRow,
} from '../wallet-card-model';
import { t } from '../i18n';
import { currentLang } from '../lang';

/**
 * « CE QUE LA CAISSE FAIT DU PORTEFEUILLE » — Intégrations → Wallet (maquette, section `integIsWallet`).
 *
 * UTILITÉ : le magasin règle ici, avec son connecteur, les deux interrupteurs du wallet et le gabarit
 * de coupon que reçoit chaque type de titre. Tout ce que la carte AFFICHE vient de `walletCardView`,
 * éprouvé sans navigateur ; ce composant ne fait que le dessiner et envoyer les gestes.
 *
 * ⚠️ CHAQUE GESTE S'ENREGISTRE AU CLIC, comme le dessin — mais l'écran ne bascule qu'à la RÉPONSE du
 * serveur (`commit`) : un interrupteur basculé d'avance resterait allumé sur un refus (§23.0).
 * ⚠️ LE MENU EST LE PANNEAU ANCRÉ PARTAGÉ (`Popover`) : Échap, clic extérieur, focus rendu, placement
 * borné à la fenêtre. La maquette ouvre ses deux dernières lignes vers le haut parce que son panneau
 * est posé DANS la carte ; le panneau partagé se place d'après la fenêtre et monte exactement quand
 * la place manque dessous.
 * ⚠️ « DÉJÀ PRIS » EST UNE COURTOISIE : c'est le serveur qui refuse un gabarit posé sur deux types
 * (`DISTINCT_SETTING_GROUPS`, contrat), quelle que soit la porte. Le motif de ce refus atteint le toast.
 */
export function WalletCard({ withoutStore, reloadKey, connection }: {
  withoutStore: boolean;
  /** Change quand la connexion se relit : la liste des gabarits se relit avec elle. */
  reloadKey: string;
  connection: WalletConnection;
}) {
  const { rows, error, byName, busy, commit } = useStoreSettingsDraft(!withoutStore);
  const lists = useServedLists(WALLET_SETTINGS, !withoutStore, reloadKey);
  const [openRow, setOpenRow] = useState<CouponTitleType | null>(null);
  // ⚠️ UN ENREGISTREMENT EN COURS REFERME LE MENU, il ne le masque pas : masqué seulement, il se rouvrirait
  //    de lui-même à la fin de l'enregistrement, et prendrait le focus.
  useEffect(() => { if (busy) setOpenRow(null); }, [busy]);

  const values: Record<string, string | null> = {};
  for (const s of WALLET_SETTINGS) values[s.name] = byName.get(s.name)?.value ?? null;
  const view = walletCardView({ values, templates: lists.wallet_coupon_templates, connection });
  /** Un réglage que le catalogue ne sert pas, ou que l'éditeur a verrouillé, ne s'écrit pas d'ici. */
  const locked = (name: string): boolean => {
    const row = byName.get(name);
    return !row || gouvernanceDe(name, row.scope) === 'verrouille';
  };
  const saved = () => t('{n} réglage enregistré — il descendra aux caisses au prochain cycle.', { n: 1 });

  const toggleEnabled = async () => {
    if (busy || locked('wallet.enabled')) return;
    if (view.enabled) {
      const ok = await confirm({
        title: t('Désactiver le wallet en caisse ?'),
        // ⚠️ CE QUE LE SERVEUR ARRÊTE VRAIMENT : tout ce qui touche un portefeuille — cartes, coupons, soldes et
        //    consommations poussés aux pass, notifications de campagne. Le texte de la maquette taisait la mise
        //    à jour des pass déjà émis.
        message: t('Plus rien ne partira vers les portefeuilles — ni carte, ni coupon, ni notification de campagne — et les pass déjà émis cesseront d’être mis à jour, soldes et coupons consommés compris, jusqu’à la réactivation.'),
        confirmLabel: t('Désactiver'),
        cancelLabel: t('Garder'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    await commit({ 'wallet.enabled': view.enabled ? '0' : '1' }, saved());
  };
  const toggleAuto = async () => {
    if (busy || !view.enabled || locked('wallet.auto')) return;
    await commit({ 'wallet.auto': view.auto ? '0' : '1' }, saved());
  };

  /** Pose (ou retire) le gabarit d'un type — et dit ce qui en découle vraiment. */
  const pick = async (row: WalletTemplateRow, templateId: string) => {
    setOpenRow(null);
    if (busy || locked(row.name) || templateId === row.value) return;
    const label = t(COUPON_TYPE_LABELS[row.type]);
    const message = templateId === '' ? t('{type} — plus aucun pass ne part pour ce type', { type: label })
      // ⚠️ LE TOAST NE PROMET PAS UN DÉPART QUI N'AURA PAS LIEU : wallet éteint ou connexion absente,
      //    le gabarit est bien réglé, et rien ne part pour autant.
      : !view.enabled ? t('{type} — gabarit réglé ; rien ne partira tant que le wallet est désactivé', { type: label })
      : connection === 'disconnected' ? t('{type} — gabarit réglé ; rien ne partira tant que la connexion n’est pas établie', { type: label })
      : connection !== 'connected' ? t('{type} — gabarit réglé ; la connexion n’a pas pu être vérifiée', { type: label })
      : t('{type} — gabarit réglé, les titres partiront au prochain cycle', { type: label });
    await commit({ [row.name]: templateId }, message);
  };

  const shared = sharedGroups(view.rows);

  return (
    <div className="dcard" style={{ overflow: 'visible', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 17px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 220 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Ce que la caisse fait du portefeuille')}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>
            {t('Activé, proposé aux nouveaux clients — et le gabarit de coupon que reçoit chaque type de titre.')}
          </span>
        </span>
        {rows !== null && !withoutStore && error == null ? <HeadPill head={view.head} /> : null}
      </div>

      {withoutStore ? (
        <div style={{ padding: '14px 17px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
          {t('Les réglages se lisent sur un magasin actif — choisissez d’abord le magasin à administrer.')}
        </div>
      ) : error != null ? (
        <EmptyState icon="alert" title={t('Réglages indisponibles')} note={error} />
      ) : rows === null ? (
        <div style={{ padding: '14px 17px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Chargement…')}</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', borderBottom: '1px solid var(--ink-100)' }}>
            {WALLET_SWITCHES.map((s, i) => {
              const on = s.name === 'wallet.enabled' ? view.enabled : view.auto;
              // ⚠️ La proposition aux nouveaux clients n'a pas d'effet wallet éteint : grisée, comme le dessin.
              const inert = s.name === 'wallet.auto' && !view.enabled;
              const blocked = busy || locked(s.name) || inert;
              return (
                <button
                  key={s.name} type="button" role="switch" aria-checked={on} aria-disabled={blocked || undefined}
                  onClick={() => { void (s.name === 'wallet.enabled' ? toggleEnabled() : toggleAuto()); }}
                  className="oz-wallet-switch"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '13px 17px', border: 'none',
                    borderRight: i === 0 ? '1px solid var(--ink-100)' : 'none', background: 'transparent',
                    textAlign: 'left', font: 'inherit', cursor: blocked ? 'default' : 'pointer', opacity: inert ? 0.45 : 1,
                  }}
                >
                  <Switch on={on} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{t(s.label)}</span>
                    <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 1, lineHeight: 1.45 }}>{t(s.hint)}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ padding: '12px 17px 4px' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              {t('Gabarit de coupon par type de titre')}
            </span>
            <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3, lineHeight: 1.5 }}>
              <Trans
                k='Dans le portefeuille du client, un chèque cadeau et un bon de fidélité doivent se distinguer. Les gabarits sont ceux de <1>votre compte Eldowallet</1> — <2>vide = ce type ne part pas</2>, jamais « le premier venu ».'
                parts={[strong, strong]}
              />
            </span>
          </div>

          <div style={{ padding: '8px 17px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {view.rows.map((row) => (
              <TemplateRow
                key={row.type} row={row} open={openRow === row.type} disabled={busy || locked(row.name)}
                onOpenChange={(o) => setOpenRow(o ? row.type : null)}
                onPick={(id) => { void pick(row, id); }}
              />
            ))}
            {shared.map((group) => (
              <div key={group.join('|')} style={{ fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>
                {t('Même gabarit pour {types} : dans son portefeuille, le client ne les distinguera pas. Choisissez-en un autre pour l’un d’eux.', {
                  // La liste dans la langue du compte (« A et B », « A and B ») : des guillemets français et un
                  // séparateur écrits ici suivraient la phrase traduite.
                  types: new Intl.ListFormat(currentLang(), { type: 'conjunction' }).format(group.map((type) => t(COUPON_TYPE_LABELS[type]))),
                })}
              </div>
            ))}
          </div>

          <div style={{ padding: '10px 17px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)', borderRadius: '0 0 var(--r-md) var(--r-md)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
            <Trans
              k='Le <1>coupon de remise</1> d’une campagne n’est pas ici : il ne naît pas d’un geste de caisse. Le wallet <2>affiche</2>, la caisse <3>consomme</3> — le pass porte votre code-barres, scanner le téléphone est le même geste que scanner le papier. Les pass partent automatiquement, pour les clients qui ont consenti.'
              parts={[strong, strong, strong]}
            />{' '}
            {t('Réglages serveur :')} <span style={{ fontFamily: 'var(--font-mono)' }}>{TEMPLATE_KEY_PATTERN}</span>.
          </div>
        </>
      )}
    </div>
  );
}

/** La forme des clés de gabarit, telle que la maquette la montre en pied de carte. */
const TEMPLATE_KEY_PATTERN = 'wallet.coupon_template.<type>';

const strong = (x: string) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>;

/**
 * LES GROUPES DE TYPES QUI PARTAGENT UN GABARIT — un seul avertissement par groupe, pas un par ligne.
 * ⚠️ Un doublon HÉRITÉ (posé avant que le serveur ne le refuse) reste possible : l'écran le montre au
 *    lieu de le taire, et le prochain choix sur l'une des lignes le tranche.
 */
function sharedGroups(rows: readonly WalletTemplateRow[]): CouponTitleType[][] {
  const seen = new Set<string>();
  const out: CouponTitleType[][] = [];
  for (const r of rows) {
    if (r.state !== 'shared') continue;
    const group = [r.type, ...r.sharedWith];
    const id = [...group].sort().join('|');
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(group);
  }
  return out;
}

/** L'interrupteur du dessin : 38×22, curseur blanc de 16 px, poussé à droite quand il est allumé. */
function Switch({ on }: { on: boolean }) {
  return (
    <span aria-hidden style={{
      width: 38, height: 22, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-500)' : 'var(--ink-200)',
      display: 'flex', alignItems: 'center', justifyContent: on ? 'flex-end' : 'flex-start', padding: '0 3px',
      flexShrink: 0, transition: 'background .15s', boxSizing: 'border-box',
    }}>
      <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff' }} />
    </span>
  );
}

/** La pastille de tête : ce qui part, pour tout le magasin. */
function HeadPill({ head }: { head: WalletHead }) {
  const tone = head.kind === 'off' || head.kind === 'checking' ? { bg: 'var(--ink-100)', fg: 'var(--ink-600)' }
    : head.kind === 'some' ? { bg: 'var(--teal-100)', fg: 'var(--teal-800)' }
    : { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' };
  const label = head.kind === 'off' ? t('Désactivé — rien ne part')
    : head.kind === 'disconnected' ? t('Connexion à établir — rien ne part')
    : head.kind === 'checking' ? t('Lecture de la connexion…')
    : head.kind === 'unknown' ? t('Connexion illisible — rien n’est garanti')
    : head.kind === 'none' ? t('Activé — aucun titre ne part encore')
    : t('{n} types sur {total} partent', { n: head.n, total: head.total });
  return (
    <span style={{ height: 24, display: 'inline-flex', alignItems: 'center', padding: '0 9px', borderRadius: 'var(--r-pill)', background: tone.bg, color: tone.fg, fontSize: 'var(--t-note)', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

/** Libellé et teintes de l'état d'une ligne — la pastille de droite. */
const ROW_STATES: Record<WalletRowState, { label: () => string; bg: string; fg: string; dot: string }> = {
  off: { label: () => t('Wallet désactivé'), bg: 'var(--ink-100)', fg: 'var(--ink-500)', dot: 'var(--ink-300)' },
  unset: { label: () => t('Ne part pas'), bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', dot: 'var(--rose)' },
  disconnected: { label: () => t('En attente de connexion'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', dot: 'var(--amber)' },
  unverified: { label: () => t('Connexion non vérifiée'), bg: 'var(--ink-100)', fg: 'var(--ink-600)', dot: 'var(--ink-300)' },
  missing: { label: () => t('Gabarit introuvable'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', dot: 'var(--amber)' },
  shared: { label: () => t('Gabarit partagé'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', dot: 'var(--amber)' },
  sent: { label: () => t('Part automatiquement'), bg: 'var(--teal-100)', fg: 'var(--teal-800)', dot: 'var(--teal-500)' },
};

/** Une ligne de type de titre : monogramme, menu du gabarit, état. */
function TemplateRow({ row, open, disabled, onOpenChange, onPick }: {
  row: WalletTemplateRow; open: boolean; disabled: boolean;
  onOpenChange: (open: boolean) => void; onPick: (templateId: string) => void;
}) {
  const label = t(COUPON_TYPE_LABELS[row.type]);
  // ⚠️ Le monogramme vire à la marque quand un gabarit est RÉGLÉ et n'est pas connu pour cassé.
  const configured = row.value !== '' && row.state !== 'missing';
  const state = ROW_STATES[row.state];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) minmax(220px, 1.4fr) 128px', gap: 12, alignItems: 'center', padding: '9px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span aria-hidden style={{
          width: 30, height: 30, borderRadius: 'var(--r-ctrl)', flexShrink: 0,
          background: configured ? 'var(--teal-100)' : 'var(--ink-100)', color: configured ? 'var(--teal-800)' : 'var(--ink-500)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', fontWeight: 800,
        }}>{couponBadge(label)}</span>
        <span style={{ minWidth: 0, fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{label}</span>
      </span>

      <Popover
        open={open && !disabled}
        onOpenChange={(o) => onOpenChange(o && !disabled)}
        id={`bo-wallet-template-${row.type}`}
        label={t('Gabarit du type « {type} »', { type: label })}
        role="menu"
        // ⚠️ LE PANNEAU A LE FOCUS À L'OUVERTURE : ↓ y entre sur « Aucun », ↑ sur le dernier gabarit.
        onPanelKeyDown={(e) => rovingMenuEntry(e)}
        geom={{ width: 'trigger' }}
        style={{ padding: 5 }}
        trigger={(api) => (
          <button
            {...api} type="button" aria-disabled={disabled || undefined}
            // ⚠️ UN NOM QUI DIT LE TYPE : sans lui, son seul contenu (« Aucun gabarit — ne part pas ») rendrait
            //    quatre boutons identiques pour un lecteur d'écran.
            aria-label={t('Gabarit du type « {type} » : {valeur}', { type: label, valeur: pickedText(row) })}
            className={`oz-wallet-pick${open ? ' oz-wallet-pick--open' : ''}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 36, padding: '0 11px', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', textAlign: 'left', font: 'inherit', cursor: disabled ? 'default' : 'pointer', minWidth: 0 }}
          >
            <PickedValue row={row} />
            <Icon name="chevronD" size={13} stroke={2.2} color="var(--ink-400)" />
          </button>
        )}
      >
        {({ close }) => <TemplateMenu row={row} onPick={(id) => { close(); onPick(id); }} />}
      </Popover>

      <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{ height: 22, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 8px', borderRadius: 'var(--r-pill)', background: state.bg, color: state.fg, fontSize: 'var(--t-sub)', fontWeight: 700, whiteSpace: 'nowrap' }}>
          <span style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: state.dot }} />
          {state.label()}
        </span>
      </span>
    </div>
  );
}

/**
 * CE QUE LE MENU FERMÉ AFFICHE.
 * ⚠️ UN GABARIT RÉGLÉ QU'ON NE RETROUVE PAS RESTE AFFICHÉ, par son identifiant : l'effacer ferait
 *    croire à « aucun gabarit » alors que le serveur en enverrait un.
 */
function PickedValue({ row }: { row: WalletTemplateRow }) {
  const base: CSSProperties = { flex: 1, minWidth: 0, fontSize: 'var(--t-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  if (row.template) return <span style={{ ...base, fontWeight: 600, color: 'var(--ink-900)' }}>{pickedText(row)}</span>;
  if (row.value === '') return <span style={{ ...base, fontWeight: 500, color: 'var(--ink-400)' }}>{pickedText(row)}</span>;
  return (
    <span style={{ ...base, fontWeight: 600, fontFamily: 'var(--font-mono)', color: row.state === 'missing' ? 'var(--amber-fg)' : 'var(--ink-900)' }}>
      {pickedText(row)}
    </span>
  );
}

/** Le texte du menu fermé — le même pour l'œil et pour le nom accessible du bouton. */
function pickedText(row: WalletTemplateRow): string {
  if (row.template) return row.template.name;
  return row.value === '' ? t('Aucun gabarit — ne part pas') : row.value;
}

/** Le contenu du menu : « aucun », puis ce que le compte Eldowallet porte — ou pourquoi il ne porte rien. */
function TemplateMenu({ row, onPick }: { row: WalletTemplateRow; onPick: (templateId: string) => void }) {
  const itemStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 9px', border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', textAlign: 'left', font: 'inherit', cursor: 'pointer' };
  const notice: CSSProperties = { padding: '10px 9px 8px', fontSize: 'var(--t-dense)', lineHeight: 1.5 };
  // ⚠️ « AUCUN » PUIS LES GABARITS : les flèches parcourent les uns après l'autre, dans l'ordre affiché.
  const count = 1 + (row.menu.kind === 'loading' || row.menu.kind === 'error' || row.menu.kind === 'empty' ? 0 : row.menu.options.length);
  return (
    <div role="none">
      {/* ⚠️ « AUCUN » TOUJOURS EN TÊTE : ne pas envoyer est un choix légitime — c'est même le défaut. */}
      <button type="button" role="menuitemradio" aria-checked={row.value === ''} className="oz-wallet-opt" style={itemStyle} onClick={() => onPick('')}
        {...rovingMenuItem({ index: 0, count })}>
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--rose-fg)' }}>{t('Aucun — ce type ne part pas')}</span>
      </button>
      {row.menu.kind === 'loading' ? (
        <div style={{ ...notice, color: 'var(--ink-500)' }}>{t('Chargement…')}</div>
      ) : row.menu.kind === 'error' ? (
        // ⚠️ LE MOTIF RÉEL DU SERVEUR, jamais « indisponible » seul : c'est lui qui dit où corriger.
        <div style={{ ...notice, color: 'var(--amber-fg)' }}>
          <strong>{t('Liste indisponible')}</strong> — {row.menu.message}
        </div>
      ) : row.menu.kind === 'empty' ? (
        <div style={{ ...notice, color: 'var(--ink-500)' }}>{t(row.menu.note)}</div>
      ) : (
        row.menu.options.map((o, i) => {
          const taker = o.takenBy ? t(COUPON_TYPE_LABELS[o.takenBy]) : null;
          const item = (
            <button
              key={o.id} type="button" role="menuitemradio" aria-checked={o.id === row.value}
              {...rovingMenuItem({ index: i + 1, count })}
              // ⚠️ `aria-disabled` ET NON `disabled` : un gabarit pris reste lisible au clavier, avec son motif.
              aria-disabled={taker !== null || undefined}
              className="oz-wallet-opt"
              style={{ ...itemStyle, opacity: taker ? 0.5 : 1, cursor: taker ? 'default' : 'pointer' }}
              onClick={() => { if (!taker) onPick(o.id); }}
            >
              <span aria-hidden style={{
                width: 26, height: 18, borderRadius: 4, flexShrink: 0,
                // ⚠️ SANS COULEUR CONNUE, UNE PASTILLE NEUTRE ET POINTILLÉE : on n'invente pas la teinte d'un gabarit.
                background: o.color ?? 'var(--ink-100)', border: o.color ? 'none' : '1px dashed var(--ink-300)', boxSizing: 'border-box',
              }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{o.name}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>
                  {taker ? t('déjà : {type}', { type: taker }) : o.id}
                </span>
              </span>
              {taker ? (
                <span style={{ height: 18, display: 'inline-flex', alignItems: 'center', padding: '0 6px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-500)', fontSize: 'var(--t-micro)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {t('déjà pris')}
                </span>
              ) : null}
            </button>
          );
          return taker ? (
            <Tooltip key={o.id} display="block" label={t('Gabarit déjà pris')} content={t('Utilisé par « {type} » — retirez-le là-bas pour le prendre ici', { type: taker })}>
              {item}
            </Tooltip>
          ) : item;
        })
      )}
    </div>
  );
}
